<script setup lang="ts">
import { useSlots } from "vue";

interface ClockBadgeProps {
  label?: string;
  time?: string;
  iconSrc?: string;
  svgPath?: string;
  backgroundColor?: string;
  textColor?: string;
  textOnly?: boolean;
}

const props = withDefaults(defineProps<ClockBadgeProps>(), {
  label: "Pre-work",
  time: "21:43",
  svgPath:
    "M12 6v6l4 2 M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z",
  backgroundColor: "bg-blue-100",
  textColor: "text-cyan-900",
  textOnly: false
});

const slots = useSlots();
const hasIconSlot = !!slots.icon;
</script>

<template>
  <article
    class="flex items-center rounded-full"
    :class="props.backgroundColor"
    style="max-width: fit-content"
  >
    <div class="px-2"></div>

    <span
      class="self-stretch my-auto text-xs font-bold leading-none uppercase"
      :class="[
        props.textColor,
        { 'mr-1': !textOnly, 'mr-1 mt-2 mb-2 ': textOnly }
      ]"
    >
      {{ props.label }}
    </span>

    <template v-if="!textOnly">
      <div
        class="shrink-0 self-stretch my-auto w-0 h-7 mx-1.5 border border-white border-solid"
      ></div>

      <!-- Icon section with multiple options -->
      <div
        class="flex items-center self-stretch ml-2 my-auto w-[18px]"
        :class="props.textColor"
      >
        <!-- Option 1: Use slot if provided -->
        <slot v-if="hasIconSlot" name="icon"></slot>

        <!-- Option 2: Use iconSrc if provided (for backward compatibility) -->
        <img
          v-else-if="props.iconSrc"
          :src="props.iconSrc"
          class="object-contain self-stretch my-auto aspect-square w-[18px]"
          alt="Icon"
        />

        <!-- Option 3: Default or custom SVG path -->
        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="object-contain self-stretch my-auto aspect-square w-[18px]"
        >
          <path :d="props.svgPath" />
        </svg>
      </div>

      <time
        class="self-stretch my-auto ml-2 text-xs font-medium leading-none whitespace-nowrap"
        :class="props.textColor"
      >
        {{ props.time }}
      </time>
    </template>

    <div class="px-2"></div>
  </article>
</template>
